import type { RouteSegment } from '@/api/main/types.gen'
import { isEmpty, last } from 'lodash-es'

export type VisitPickerAction = 'edit' | 'delete'

/** 1-based indices of every visit to `nodeId` along `pathNodes` (supports multi-visit). */
export function pathVisitNumbers(pathNodes: string[], nodeId: string): number[] {
  return pathNodes.map((id, i) => (id === nodeId ? i + 1 : undefined)).filter((n): n is number => n !== undefined)
}

/**
 * Editable visits exclude the current path end: truncating/editing from the last node
 * is handled by delete-last / append, not the visit picker.
 */
export function editableVisitNumbers(pathNodes: string[], nodeId: string): number[] {
  return pathVisitNumbers(pathNodes, nodeId).filter(pos => pos < pathNodes.length)
}

export function shouldShowVisitPicker(
  action: VisitPickerAction,
  editableVisits: number[],
  isLastNode: boolean
): boolean {
  if (editableVisits.length <= 1) return false
  if (action === 'delete' && isLastNode) return false
  return true
}

/**
 * Assigns visit numbers to intermediate nodes on a shortest-path hint, continuing after
 * `pathNodesLength`. Skips the hint source and reconnect (final exit) — those reuse path
 * / reconnect labels instead.
 */
export function buildHintVisitNumbers(
  shortestPathHint: RouteSegment[] | null | undefined,
  pathNodesLength: number
): Map<string, number> {
  if (isEmpty(shortestPathHint)) return new Map()

  const map = new Map<string, number>()
  const reconnectNodeId = last(shortestPathHint)!.exit_node_id
  const sourceNodeId = shortestPathHint![0]!.entrance_node_id
  let pos = pathNodesLength + 1

  for (const seg of shortestPathHint!) {
    if (seg.exit_node_id !== sourceNodeId && seg.exit_node_id !== reconnectNodeId && !map.has(seg.exit_node_id)) {
      map.set(seg.exit_node_id, pos++)
    }
  }
  return map
}

/** Visit number for the hint reconnect node: one past path length plus intermediate hint nodes. */
export function hintReconnectVisitNumber(
  shortestPathHint: RouteSegment[] | null | undefined,
  pathNodesLength: number,
  hintVisitNumbers: Map<string, number>
): number | undefined {
  if (isEmpty(shortestPathHint)) return undefined
  return pathNodesLength + hintVisitNumbers.size + 1
}

export function formatNodeLabel(pathVisits: number[], hintVisit?: number): string {
  const parts = pathVisits.map(String)
  if (hintVisit !== undefined) parts.push(String(hintVisit))
  return parts.join(',')
}
